import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { flowsService } from '@/services/flows.service';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  useToast,
} from '@caruso/ui';
import {
  PlayCircle,
  PauseCircle,
  XCircle,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  User,
  GitBranch,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Eye,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function FlowExecutions() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [flowIdFilter, setFlowIdFilter] = useState<string | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['flow-executions', { page, status: statusFilter, flowId: flowIdFilter }],
    queryFn: () =>
      flowsService.getExecutions({
        page,
        limit: 20,
        status: statusFilter,
        flowId: flowIdFilter,
      }),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => flowsService.pauseExecution(id),
    onSuccess: () => {
      toast({ title: 'Execução pausada', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['flow-executions'] });
    },
    onError: () => {
      toast({ title: 'Erro ao pausar execução', variant: 'destructive' });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (id: string) => flowsService.resumeExecution(id),
    onSuccess: () => {
      toast({ title: 'Execução retomada', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['flow-executions'] });
    },
    onError: () => {
      toast({ title: 'Erro ao retomar execução', variant: 'destructive' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => flowsService.cancelExecution(id),
    onSuccess: () => {
      toast({ title: 'Execução cancelada', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['flow-executions'] });
    },
    onError: () => {
      toast({ title: 'Erro ao cancelar execução', variant: 'destructive' });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => flowsService.retryExecution(id),
    onSuccess: () => {
      toast({
        title: 'Execução reiniciada',
        description: 'A execução será processada novamente em breve',
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['flow-executions'] });
    },
    onError: () => {
      toast({ title: 'Erro ao reiniciar execução', variant: 'destructive' });
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { className: string; icon: any; label: string }> = {
      pending: {
        className: 'bg-brand-ivory/10 text-brand-ivory/70 border-brand-ivory/20',
        icon: Clock,
        label: 'Pendente'
      },
      running: {
        className: 'bg-brand-gold/10 text-brand-gold border-brand-gold/20',
        icon: PlayCircle,
        label: 'Em andamento'
      },
      completed: {
        className: 'bg-green-500/10 text-green-400 border-green-500/20',
        icon: CheckCircle,
        label: 'Concluído'
      },
      failed: {
        className: 'bg-red-500/10 text-red-400 border-red-500/20',
        icon: AlertCircle,
        label: 'Falhou'
      },
      paused: {
        className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
        icon: PauseCircle,
        label: 'Pausado'
      },
      waiting_response: {
        className: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        icon: MessageSquare,
        label: 'Aguardando resposta'
      },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDateTime = (date: string | undefined) => {
    if (!date) return '-';
    return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Execuções de Fluxos</h1>
          <p className="text-brand-ivory/60 mt-1">
            Acompanhe o status das execuções em andamento e finalizadas
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="bg-brand-ink/50 border-brand-cigar/20">
        <CardHeader>
          <CardTitle className="text-brand-gold">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={statusFilter === undefined ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(undefined)}
            >
              Todos
            </Button>
            <Button
              variant={statusFilter === 'running' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('running')}
            >
              Em andamento
            </Button>
            <Button
              variant={statusFilter === 'completed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('completed')}
            >
              Concluídos
            </Button>
            <Button
              variant={statusFilter === 'failed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('failed')}
            >
              Falhados
            </Button>
            <Button
              variant={statusFilter === 'paused' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('paused')}
            >
              Pausados
            </Button>
            <Button
              variant={statusFilter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('pending')}
            >
              Pendentes
            </Button>
            <Button
              variant={statusFilter === 'waiting_response' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('waiting_response')}
            >
              Aguardando resposta
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Execuções */}
      <div className="space-y-4">
        {isLoading ? (
          <Card className="bg-brand-ink/50 border-brand-cigar/20">
            <CardContent className="py-12 text-center">
              <p className="text-brand-ivory/60">Carregando execuções...</p>
            </CardContent>
          </Card>
        ) : data?.data.length === 0 ? (
          <Card className="bg-brand-ink/50 border-brand-cigar/20">
            <CardContent className="py-12 text-center">
              <GitBranch className="w-12 h-12 text-brand-ivory/30 mx-auto mb-4" />
              <p className="text-brand-ivory/60">Nenhuma execução encontrada</p>
            </CardContent>
          </Card>
        ) : (
          data?.data.map((execution) => (
            <Card key={execution.id} className="bg-brand-ink/50 border-brand-cigar/20">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <CardTitle className="text-brand-gold flex items-center gap-2">
                      <GitBranch className="w-5 h-5" />
                      {execution.flow?.name || 'Fluxo sem nome'}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 text-brand-ivory/60">
                      <User className="w-4 h-4" />
                      {execution.client?.name || execution.client?.email || 'Cliente desconhecido'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(execution.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-brand-ivory/60">Criado em</p>
                    <p className="text-brand-ivory font-medium">
                      {formatDateTime(execution.createdAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-brand-ivory/60">Iniciado em</p>
                    <p className="text-brand-ivory font-medium">
                      {formatDateTime(execution.startedAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-brand-ivory/60">Finalizado em</p>
                    <p className="text-brand-ivory font-medium">
                      {formatDateTime(execution.completedAt)}
                    </p>
                  </div>
                </div>

                {execution.error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {execution.error}
                    </p>
                  </div>
                )}

                {execution._count?.messages > 0 && (
                  <div>
                    <p className="text-brand-ivory/60 text-sm mb-2">
                      Mensagens: {execution._count.messages}
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t border-brand-cigar/20">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/app/fluxos/${execution.flowId}/execucoes-lista`)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Detalhes
                  </Button>
                  {execution.status === 'running' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => pauseMutation.mutate(execution.id)}
                      disabled={pauseMutation.isPending}
                    >
                      <PauseCircle className="w-4 h-4 mr-2" />
                      Pausar
                    </Button>
                  )}
                  {execution.status === 'paused' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resumeMutation.mutate(execution.id)}
                      disabled={resumeMutation.isPending}
                    >
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Retomar
                    </Button>
                  )}
                  {(execution.status === 'running' || execution.status === 'paused') && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => cancelMutation.mutate(execution.id)}
                      disabled={cancelMutation.isPending}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                  )}
                  {execution.status === 'failed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => retryMutation.mutate(execution.id)}
                      disabled={retryMutation.isPending}
                      className="text-brand-gold hover:bg-brand-gold/10"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {retryMutation.isPending ? 'Reiniciando...' : 'Tentar Novamente'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Paginação */}
      {data && data.meta.totalPages > 1 && (
        <Card className="bg-brand-ink/50 border-brand-cigar/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-brand-ivory/60">
                Página {data.meta.page} de {data.meta.totalPages} ({data.meta.total} execuções)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.meta.totalPages}
                >
                  Próxima
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
